import React, {memo, useLayoutEffect, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {buildStyles} from "connectui/component/buildStyles";
import {useTheme} from "connectui/theme/ThemeProvider";
import {getMessages} from "redux/actions/messagesActions";
import {Waypoint} from "react-waypoint";
import _ from "lodash";
// Components
import FlexContainer from "connectui/component/layout/FlexContainer";
import ListComponent from "connectui/component/layout/list/ListComponent";
import MessageBubble from "../../conversations/messages/MessageBubble";
import GroupedNotifications from "../../conversations/messages/GroupedNotifications";
import Container from "connectui/component/layout/Container";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    loadingContainer: {
      width: "100%",
      height: "100%",
      position: "absolute",
      top: 0,
      left: 0,
      backgroundColor: theme.colors.gray50,
    },
    loadingList: {
      padding: theme.spacing(5, 4),
      flex: 1,
      width: "100%",
      overflow: "auto",
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    messagesWrapper: {
      flex: 1,
      width: "100%",
      padding: theme.spacing(5, 4, 12.5),
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    messagesContainer: {
      width: "100%",
      height: "100%",
      position: "relative",
    },
    list: {
      outline: "none",
      "&::-webkit-scrollbar": {width: "0!important"},
    },
    noScroll: {overflow: "hidden !important"},
  });

const Chat = memo(
  ({
    guest,
    hasNextPage,
    messages = [],
    setMessages = () => {},
    skeleton = {enabled: false},
  }) => {
    const {theme} = useTheme();
    const classes = useStyles(theme);
    const dispatch = useDispatch();
    const messagesWrapper = useRef(null);
    const prevProps = useRef({
      guest: null,
      messages: [],
      loading: true,
    });
    const scrollPosition = useRef(0);
    const [fetchingMore, setFetchingMore] = useState(false);
    const guestId = guest?.guest_id;

    useLayoutEffect(() => {
      let scrollToBottom = false;

      // If different guest
      if (prevProps.current.guest?.guest_id !== guest.guest_id) {
        setFetchingMore(false);
        scrollToBottom = true;
        prevProps.current.guest = guest;
      }

      // If messages were loading and now are loaded
      if (prevProps.current.loading && !skeleton.enabled) {
        scrollToBottom = true;
      }
      prevProps.current.loading = skeleton.enabled;

      // Scroll to bottom
      if (scrollToBottom && messagesWrapper.current) {
        messagesWrapper.current.scrollTop = 0;
        scrollPosition.current = 0;
      }
    }, [guest, skeleton.enabled]);

    useLayoutEffect(() => {
      if (fetchingMore && prevProps.current.messages.length < messages.length) {
        // Restore scroll position after loading more messages
        if (messagesWrapper.current) {
          messagesWrapper.current.scrollTop = scrollPosition.current;
        }
        setFetchingMore(false);
      }
      prevProps.current.messages = messages;
    }, [messages]);

    const loadMoreMessages = (lastSent) => {
      dispatch(
        getMessages({
          guestId: guestId,
          lastSent,
          onSuccess: (res) => setMessages(res),
          onError: () => setFetchingMore(false),
        }),
      );
    };

    const loadMoreRows = (index) => {
      if (!hasNextPage || fetchingMore || skeleton.enabled) {
        return;
      }

      setFetchingMore(true);
      loadMoreMessages(messages[messages.length - 1].sent);
    };

    return skeleton.enabled ? (
      <FlexContainer
        flexDirection="column"
        className={classes.loadingContainer}
      >
        <ListComponent gap={theme.spacing(10)} className={classes.loadingList}>
          {Array.from({length: 5}).map((_, index) => (
            <MessageBubble
              key={index}
              sender={index % 2 === 0 ? "user" : "other_user"}
              skeleton={{
                enabled: true,
                width: "65%",
                height: "70px",
              }}
            />
          ))}
        </ListComponent>
      </FlexContainer>
    ) : (
      <FlexContainer
        ref={messagesWrapper}
        className={classes.messagesWrapper}
        flexDirection="column-reverse"
        gap={theme.spacing(15)}
        justifyContent="flex-start"
        onScroll={(e) => (scrollPosition.current = e.target.scrollTop)}
        sx={{overflow: fetchingMore ? "hidden" : "auto"}}
      >
        {messages.map((msg, index) => {
          return !!msg.grouped ? (
            <GroupedNotifications
              key={`notif-group-${msg.start_time ?? index}`}
              msg={msg}
            />
          ) : (
            <Container key={`${msg.id}-${index}`} width="100%">
              {hasNextPage && index >= messages.length - 1 && (
                <Waypoint onEnter={() => loadMoreRows(index)} />
              )}
              <MessageBubble guest={guest} msg={msg} sender={msg.sender_type} />
            </Container>
          );
        })}

        {/* Loading more */}
        {hasNextPage && (
          <Container width="100%" padding={theme.spacing(0, 4)}>
            <MessageBubble
              sender={"user"}
              skeleton={{
                enabled: true,
                width: "65%",
                height: "70px",
              }}
            />
          </Container>
        )}
      </FlexContainer>
    );
  },
  (prevProps, nextProps) => {
    const sameHasNextPage = prevProps.hasNextPage === nextProps.hasNextPage;
    const sameGuest = prevProps.guest?.guest_id === nextProps.guest?.guest_id;
    const sameMessages =
      prevProps.messages.length === nextProps.messages.length &&
      prevProps.messages[0]?.id === nextProps.messages[0]?.id &&
      prevProps.messages[0]?.sent === nextProps.messages[0]?.sent &&
      prevProps.messages[0]?.sending_error ===
        nextProps.messages[0]?.sending_error;
    const sameSkeleton =
      prevProps.skeleton.enabled === nextProps.skeleton.enabled;
    return sameHasNextPage && sameGuest && sameMessages && sameSkeleton;
  },
);

export default Chat;
